<template>
  <div
    class="dropdown-container"
    data-pendo-gc="dropdown"
  >
    <div
      ref="dropdownTrigger"
      class="dropdown-trigger"
      :class="{ disabled }"
      @click="handleClick"
      @keydown.enter="setPositions"
      @keydown.space="setPositions"
    >
      <slot name="trigger">
        Open dropdown
      </slot>
    </div>
    <slot name="errors" />
    <component :is="animate ? 'transition-expand' : 'div'">
      <div
        v-if="open || useVShow"
        v-show="(open && useVShow) || !useVShow"
        ref="dropdownWrapper"
        class="dropdown-wrapper cr-br-medium"
        :style="{ right, left, top, bottom, position, width: wrapperWidth }"
      >
        <slot name="content" />
      </div>
    </component>
  </div>
</template>

<script lang="ts">
import TransitionExpand from './TransitionExpand.vue'

export default {
  name: 'BaseDropdown',
  components: {
    TransitionExpand,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    fixed: {
      type: Boolean,
      default: false,
    },
    useVShow: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    wrapperWidth: {
      type: String,
      default: 'auto',
    },
    animate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:open'],
  data() {
    return {
      right: '' as string | number,
      left: '' as string | number,
      top: '',
      bottom: '',
    }
  },
  computed: {
    position() {
      return this.fixed ? 'fixed' : 'absolute'
    },
  },
  mounted() {
    document.addEventListener('click', event => this.handleOutsideClick(event))
  },
  beforeUnmount() {
    document.removeEventListener('click', event => this.handleOutsideClick(event))
  },
  methods: {
    dropdownTrigger() {
      return this.$refs.dropdownTrigger as HTMLElement | null
    },
    toggleOpen() {
      this.setPositions()

      this.$emit('update:open', !this.open)
    },
    setPositions() {
      if (this.fixed) {
        this.setFixedPositions()
      } else {
        this.setAbsolutePositions()
      }
    },
    handleClick() {
      if (!this.disabled) {
        this.toggleOpen()
      }
    },
    setAbsolutePositions() {
      const trigger = this.dropdownTrigger()
      if (!trigger) return

      const triggerRect = trigger.getBoundingClientRect()
      const { x, y } = triggerRect
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight

      if (x > screenWidth / 2) {
        this.right = 0
        this.left = ''
      } else {
        this.left = 0
        this.right = ''
      }

      if (y > screenHeight / 2) {
        this.bottom = 'calc(100% + var(--space-1))'
        this.top = ''
      } else {
        this.top = 'calc(100% + var(--space-1))'
        this.bottom = ''
      }
    },
    setFixedPositions() {
      const trigger = this.dropdownTrigger()
      if (!trigger) return

      const triggerRect = trigger.getBoundingClientRect()
      const { x, y } = triggerRect
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight

      if (x > screenWidth / 2) {
        this.right = `${window.innerWidth - triggerRect.right}px`
        this.left = ''
      } else {
        this.left = `${triggerRect.left}px`
        this.right = ''
      }

      if (y > screenHeight / 2) {
        this.bottom = `calc(${window.innerHeight - triggerRect.top}px + var(--space-1))`
        this.top = ''
      } else {
        this.top = `calc(${triggerRect.bottom}px + var(--space-1))`
        this.bottom = ''
      }
    },
    handleOutsideClick(event: MouseEvent) {
      if (this.open && !this.$el.contains(event.target)) {
        this.$emit('update:open', false)
      }
    },
  },
}
</script>

<style scoped>
.dropdown-container {
  position: relative;
  display: inline-block;
}
.dropdown-wrapper {
  max-width: calc(100vw - var(--space-5));
  border: 1px solid var(--candy-wrapper-50);
  box-shadow: var(--cr-ds-medium);
  background: var(--salt);
  z-index: 100;
}
.dropdown-trigger {
  cursor: pointer;
  &.disabled {
    cursor: auto;
  }
}
</style>
